import React from 'react';
import GridItem from '../../../../layout/GridComponents/GridItem';
import CustomTable from '../../../../tables/CustomTable';
import { CustomColumn } from '../../../../../types/components/tables/tableTypes';
import { mainColors } from '../../../../../styling/theme';
import type { LiquidityModelBreakdownPageModel } from '../../../../../api/rs-hg/liquidity/hooks/queries';

interface LiquiditModelBreakdownTableProps {
  pageData: LiquidityModelBreakdownPageModel['data'];
  volumeOrDays: 'days' | 'volume';
  participationRate: string;
}

export interface LiquiditModelBreakdownTableData {
  position_name: string;
  exposure: number;
  historical_volume: number;
  heuristic_volume: number;
  pebl_peel_volume: number;
  applied_volume: number;
  historical_days: number;
  heuristic_days: number;
  pebl_peel_days: number;
  applied_days: number;
}

const detailColumns = (
  volumeOrDays: 'days' | 'volume',
): CustomColumn<LiquiditModelBreakdownTableData>[] => [
    {
      title: 'Position Name',
      field: 'position_name',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
    {
      title: 'Exposure',
      field: 'exposure',
      render: (rowData) =>
        typeof rowData.exposure === 'number'
          ? rowData.exposure.toLocaleString()
          : '',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
    {
      title: 'Historic',
      field: volumeOrDays === 'volume' ? 'historical_volume' : 'historical_days',
      render: (rowData) =>
        volumeOrDays === 'volume'
          ? typeof rowData.historical_volume === 'number'
            ? rowData.historical_volume.toLocaleString()
            : ''
          : typeof rowData.historical_days === 'number'
            ? rowData.historical_days.toLocaleString()
            : '',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
    {
      title: 'Heuristic',
      field: volumeOrDays === 'volume' ? 'heuristic_volume' : 'heuristic_days',
      render: (rowData) =>
        volumeOrDays === 'volume'
          ? typeof rowData.heuristic_volume === 'number'
            ? rowData.heuristic_volume.toLocaleString()
            : ''
          : typeof rowData.heuristic_days === 'number'
            ? rowData.heuristic_days.toLocaleString()
            : '',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
    {
      title: 'PEBL/PEEL',
      field: volumeOrDays === 'volume' ? 'pebl_peel_volume' : 'pebl_peel_days',
      render: (rowData) =>
        volumeOrDays === 'volume'
          ? typeof rowData.pebl_peel_volume === 'number'
            ? rowData.pebl_peel_volume.toLocaleString()
            : ''
          : typeof rowData.pebl_peel_days === 'number'
            ? rowData.pebl_peel_days.toLocaleString()
            : '',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
    {
      title: 'Applied',
      field: volumeOrDays === 'volume' ? 'applied_volume' : 'applied_days',
      render: (rowData) =>
        volumeOrDays === 'volume'
          ? typeof rowData.applied_volume === 'number'
            ? rowData.applied_volume.toLocaleString()
            : ''
          : typeof rowData.applied_days === 'number'
            ? rowData.applied_days.toLocaleString()
            : '',
      cellStyle: {
        textAlign: 'center',
        borderLeft: `1px solid ${mainColors.mainBlue}`,
      },
      headerStyle: {
        textAlign: 'center',
        borderLeft: `1px solid ${mainColors.mainBlue}`,
      },
    },
  ];

const LiquiditModelBreakdownTable: React.FC<
  LiquiditModelBreakdownTableProps
> = ({ pageData, volumeOrDays }) => {
  const builtData = pageData as LiquiditModelBreakdownTableData[];

  return (
    <GridItem xs={12} card>
      <div style={{ overflowX: 'auto', width: '100%' }}>
        <CustomTable<LiquiditModelBreakdownTableData>
          columns={detailColumns(volumeOrDays)}
          showToolbar
          data={builtData}
          title={
            volumeOrDays === 'volume'
              ? 'Liquidity Model Breakdown - Volume'
              : 'Liquidity Model Breakdown - Days to Liquidate'
          }
          options={{
            paging: false,
            search: true,
            exportButton: true,
            maxBodyHeight: 'calc(100vh - 12.5rem)',
          }}
        />
      </div>
    </GridItem>
  );
};

export default LiquiditModelBreakdownTable;
